import { gql, useMutation, useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import { showToast } from 'components/common/CustomToast';
import AccountContext from 'context/AccountContext';
import ParkingContext from 'context/ParkingContext';
import { CREATE_CHARGING_SESSION_MUTATION } from 'graphql/mutations/createChargingSesssion';
import { CREATE_PARKING_SESSION } from 'graphql/mutations/createParkingSession';
import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { country } from 'states/persistInStorage';
import { useParkingStore } from 'stores/parkingStore';
import { getParkzoneState, useParkzoneStore } from 'stores/parkzoneStore';
import { usePaymentStore } from 'stores/paymentStore';
import { useSearchStore } from 'stores/searchStore';
import { useUiStore } from 'stores/uiStore';
import { ParkingZoneType } from 'types/common';
import {
  Maybe,
  Mutation,
  MutationCreateChargingSessionV3Args,
  MutationCreateParkingSessionArgs,
  ParkingSession,
  PaymentType,
  SelectedSegment,
} from 'types/generatedSchemaTypes';
import { ParkNavProps } from 'types/navigation';
import { updateCache } from 'utils/cacheUtils';
import Logger, { FlowStatus, UserActionType, UserFlowName } from 'utils/Logger';
import { addRecentParkingWithSegment } from 'utils/parkUtils';
import {
  getCAbenefit,
  isCard,
  isCompanyBenefit,
  isCorporateAccountPaymentOption,
  isExternalPayment,
} from 'utils/paymentUtils';
import useCreateExternalPayment from './useCreateExternalPayment';
import useGetPoolingInfo from './useGetPoolingInfo';
import useMinimumLoadingTime from './useMinimumLoadingTime';
import useOngoingChargingSession from './useOngoingChargingSession';
import useOngoingParkingSession from './useOngoingParkingSession';
import useSelectedCompanyBenefit from './useSelectedCompanyBenefit';

type CreateSessionProps = {
  parkingZoneType?: ParkingZoneType;
  onErrorCallback?: () => void;
};

// Module-level in-flight guard: shared across all hook instances to prevent
// duplicate mutations when multiple components call createParking/createCharging
// simultaneously with the same parameters (e.g., two StartParkingButton instances).
const parkingInflightPromises = new Map<string, Promise<any>>();
const chargingInflightPromises = new Map<string, Promise<any>>();

const useCreateSession = ({
  parkingZoneType,
  onErrorCallback,
}: CreateSessionProps) => {
  const {
    selectedEvCharger,
    parkingZoneResult: parkingZone,
    setSelectedEvCharger,
  } = useContext(ParkingContext) || {};
  const {
    customer: { contactEmail },
  } = useContext(AccountContext);

  const { parkingConditions, selectedParkingVehiclePlate, resetParkingState } =
    useParkingStore((state) => state);
  const { popTo: navigatePark } = useNavigation<ParkNavProps>();
  const { resetSearch } = useSearchStore((state) => state);

  const { selectedParkingZoneId, paymentMethod, endTime } = parkingConditions;
  const resumableSessionInfo = useParkingStore(
    (state) => state.resumableSession
  );
  const { t } = useTranslation();

  const selectedTariff = useParkzoneStore((state) => state.selectedTariffId);
  const tariffName = useParkzoneStore((state) => state.selectedTariffName);
  const countryCode = useReactiveVar(country);
  const customerSegment = {
    uid: selectedTariff,
    countryCode,
  } as SelectedSegment;
  const { poolingGroup } = useGetPoolingInfo(selectedParkingZoneId);

  const { id: stationId, evse, chargingLocationId } = selectedEvCharger || {};

  const isCreditCard = isCard(paymentMethod);

  const externalPaymentMethod = isExternalPayment(paymentMethod);

  const { addOngoingSessionToCache } = useOngoingParkingSession(false, true);
  const { ongoingChargingSessionRefetch } = useOngoingChargingSession();
  const [parkingSuccess, setParkingSuccess] = useState<boolean>(false);
  const [chargingSuccess, setChargingSuccess] = useState<boolean>(false);
  // Local to this hook instance. Kept out of the store during render — the
  // store entry is written in onCompleted, once there is a session id to key in
  // so several mounted instances no longer overwrite each other.
  const parkingFlowId = useMemo(() => Logger.generateFlowId(), []);

  // Default exceptionType is PARKING_SESSION so it is not required here. Added for clarity
  const {
    corporateAccountTransactionFee: parkingCorporateAccountTransactionFee,
    parkingCorporateAccountDiscountPercent,
  } = useSelectedCompanyBenefit(selectedParkingZoneId, 'PARKING_SESSION');

  const {
    corporateAccountTransactionFee: chargingCorporateAccountTransactionFee,
    parkingCorporateAccountDiscountPercent:
      evParkingTimeCorporateAccountDiscountPercent,
    evEnergyDiscountPercent,
  } = useSelectedCompanyBenefit(selectedParkingZoneId, 'CHARGING_SESSION');
  const selectedChargingSegment = useParkzoneStore(
    (state) => state.selectedChargingPricingInfoSegment
  );

  const onParkingSuccess = async () => {
    setParkingSuccess(true);
    useParkingStore.getState().setResumeSessionPossible(false);
    if (parkingZoneType !== 'PARK_AND_CHARGE') {
      navigatePark('park');
    } else {
      await createCharging();
    }
    resetParkingState();
  };

  const { createExternalPayment } = useCreateExternalPayment({
    onFreeComplete: onParkingSuccess,
  });

  const getPaymentType = () => {
    if (externalPaymentMethod) {
      return paymentMethod?.externalPaymentType;
    }
    if (isCreditCard) {
      return 'CREDIT_CARD';
    }
    if (
      isCompanyBenefit(paymentMethod) ||
      isCorporateAccountPaymentOption(paymentMethod)
    ) {
      return 'CORPORATE_ACCOUNT';
    }
    if (paymentMethod?.method === 'MOBILEPAY') {
      return 'MOBILEPAY';
    }
    return 'NONE';
  };

  const isResumableSession = () => {
    if (resumableSessionInfo) {
      return {
        startDate: resumableSessionInfo.startDate,
        resumedSessionId: resumableSessionInfo.sessionId,
      };
    }
  };

  const freeExternalSessionOrOtherPayment = (
    session?: Maybe<ParkingSession>
  ) => {
    const isFreeExternalSession =
      externalPaymentMethod && session?.price?.totalAmount === 0;

    return !externalPaymentMethod || isFreeExternalSession;
  };

  const [sessionMutation, { loading: createParkingLoading }] = useMutation<
    Mutation,
    MutationCreateParkingSessionArgs
  >(CREATE_PARKING_SESSION, {
    variables: {
      input: {
        type: 'START_STOP',
        cardId: isCreditCard ? (paymentMethod?.cardId as string) : null,
        customerEmail: contactEmail || '',
        endDate: endTime || new Date(),
        ...isResumableSession(),
        licensePlate: selectedParkingVehiclePlate,
        parkingZoneUid: selectedParkingZoneId,
        customerSegment: poolingGroup ? undefined : customerSegment,
        paymentType: getPaymentType(),
        corporateAccountUid: getCAbenefit(paymentMethod),
        serviceFeeAmount: parkingCorporateAccountTransactionFee,
        discountPercent: parkingCorporateAccountDiscountPercent,
      },
    },
    onCompleted: async (data) => {
      useUiStore.getState().setExpandOngoingSheet(true);
      const createdSessionId = data?.createParkingSession?.sessionId;
      // Hand this instance's flow id to the update path (stop/extend) so its
      // logs correlate with the session this instance actually created.
      if (createdSessionId) {
        useParkingStore
          .getState()
          .setParkingSessionFlowId(createdSessionId, parkingFlowId);
      }
      Logger.logUserFlow(
        UserFlowName.CreateParkingSession,
        parkingFlowId,
        FlowStatus.InProgress,
        {
          parking_zone_uid: selectedParkingZoneId,
          parking_session_id: createdSessionId || '',
        }
      );
      addRecentParkingWithSegment({
        zoneUid:
          parkingZone?.data?.parkZone?.commercialZoneView?.zoneCode || '',
        customerSegmentUid: selectedTariff,
        customerSegmentName: tariffName,
      });
      if (freeExternalSessionOrOtherPayment(data?.createParkingSession)) {
        return onParkingSuccess();
      } else {
        const description = t('parking.externalParkingDescription', {
          zone: parkingZone?.data?.parkZone?.commercialZoneView?.zoneCode,
          licencePlate: selectedParkingVehiclePlate,
        });
        usePaymentStore
          .getState()
          .setExternalPaymentSessionId(createdSessionId ?? null);
        usePaymentStore.getState().setExternalPaymentFlowLoading(true);
        createExternalPayment({
          variables: {
            input: {
              actionType: 'START_PARKING',
              countryCode: countryCode,
              paymentMethod: paymentMethod?.externalPaymentType,
              orderDescription: description,
              items: [
                {
                  parkingSessionId: data?.createParkingSession?.sessionId || '',
                  licensePlate: selectedParkingVehiclePlate,
                  type: 'START_STOP',
                },
              ],
            },
          },
        });
      }
      resetSearch();
    },
    update: (cache, { data: updateData }) => {
      if (freeExternalSessionOrOtherPayment(updateData?.createParkingSession)) {
        addOngoingSessionToCache(cache, updateData?.createParkingSession);
      }
      const sessionId = updateData?.createParkingSession?.sessionId;
      updateCache(
        cache,
        { __typename: 'ParkingSession', sessionId },
        {
          fragment: gql`
            fragment ParkingSessionFragment on ParkingSession {
              tariffName
            }
          `,
          // Read fresh at cache-update time rather than using the subscribed
          // `tariffName` above, which would be captured in this closure.
          data: { tariffName: getParkzoneState().selectedTariffName },
        }
      );
    },
    onError: (error) => {
      const errorCode = error.graphQLErrors[0]?.extensions?.code as
        | string
        | undefined;
      Logger.logUserFlow(
        UserFlowName.CreateParkingSession,
        parkingFlowId,
        FlowStatus.Failed,
        {
          error_message: error.message,
          error_code: errorCode,
          parking_zone_uid: selectedParkingZoneId,
        }
      );
      // Nothing to clear: no session was created, so no flow id was registered.
      onErrorCallback?.();
      if (errorCode === 'CARD_NOT_ACTIVE') {
        showToast('error.paymentCardNotActive', 'error');
        return;
      }
      // Temporary fix for conflict on create parking.
      // Will be removed with proper error handling in epic CPS-20294
      if (errorCode === 'DUPLICATE_PARKING_SESSION') {
        showToast('error.conflictOnCreateParking', 'error');
        return;
      }
      showToast('error.parkingStartedFailed', 'error');
    },
  });

  const [createCharging, { loading: createChargingLoading }] = useMutation<
    Mutation,
    MutationCreateChargingSessionV3Args
  >(CREATE_CHARGING_SESSION_MUTATION, {
    variables: {
      input: {
        cardId: isCreditCard ? (paymentMethod?.cardId as string) : null,
        licensePlate: selectedParkingVehiclePlate || '',
        corporateAccountUid: getCAbenefit(paymentMethod),
        chargingLocationId: chargingLocationId || '',
        countryCode: parkingZone?.data?.parkZone?.countryCode || countryCode,
        evseId: evse?.id || '',
        parkingZoneUid: selectedParkingZoneId,
        stationId: stationId || '',
        customerSegmentUid: selectedChargingSegment?.uid,
        discountPercent: evParkingTimeCorporateAccountDiscountPercent,
        serviceFee: chargingCorporateAccountTransactionFee,
        energyDiscountPercent: evEnergyDiscountPercent,
        paymentType: getPaymentType() as PaymentType,
      },
    },
    onCompleted: async () => {
      useUiStore.getState().setExpandOngoingSheet(true);
      await ongoingChargingSessionRefetch();
      setChargingSuccess(true);
      useParkingStore.getState().setSelectedSessionIdFromMultiple(null);
      setSelectedEvCharger(undefined);
      useParkingStore.getState().setSelectedOngoingSession(undefined);
      resetParkingState();
      navigatePark('park');
    },
    onError: () => {
      onErrorCallback?.();
      showToast('error.chargingStartedFailed', 'error');
    },
  });

  // Stable identity so consumers (e.g. ConnectCharger's start effect) can keep
  // it in a dependency array without it changing on every render. `sessionMutation`
  // is Apollo's stable execute fn; it reads the latest variables at call time.
  const createParking = useCallback(() => {
    Logger.logUserAction(
      UserFlowName.CreateParkingSession,
      UserActionType.Tap,
      { parkingZoneUid: selectedParkingZoneId }
    );

    // In-flight guard: prevent duplicate mutations when multiple hook instances
    // call createParking() simultaneously for the same parking zone + vehicle.
    const inflightKey = `${selectedParkingVehiclePlate}:${selectedParkingZoneId}`;
    if (parkingInflightPromises.has(inflightKey)) {
      return parkingInflightPromises.get(inflightKey)!;
    }

    const promise = sessionMutation().finally(() => {
      parkingInflightPromises.delete(inflightKey);
    });

    parkingInflightPromises.set(inflightKey, promise);
    return promise;
  }, [selectedParkingVehiclePlate, selectedParkingZoneId, sessionMutation]);

  const { showLoading: showLoadingParking } = useMinimumLoadingTime(
    createParkingLoading,
    5000
  );
  const { showLoading: showLoadingCharging } = useMinimumLoadingTime(
    createChargingLoading,
    5000
  );

  // Wrap createCharging with the same in-flight guard as createParking
  const createChargingGuarded = useCallback(async () => {
    const inflightKey = `${selectedParkingVehiclePlate}:${evse?.id || ''}`;
    if (chargingInflightPromises.has(inflightKey)) {
      return chargingInflightPromises.get(inflightKey)!;
    }

    const promise = createCharging().finally(() => {
      chargingInflightPromises.delete(inflightKey);
    });

    chargingInflightPromises.set(inflightKey, promise);
    return promise;
  }, [selectedParkingVehiclePlate, evse?.id, createCharging]);

  return {
    createParking,
    createParkingLoading: createParkingLoading || showLoadingParking,
    parkingSuccess,
    createCharging: createChargingGuarded,
    createChargingLoading: createChargingLoading || showLoadingCharging,
    chargingSuccess,
  };
};

export default useCreateSession;
